import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FaArrowLeft, FaPenFancy, FaHistory } from "react-icons/fa";
import { API_BASE_URL } from "../../config/apiConfig";
import { formatAiText } from "../../utils/formatAiText";

const authHeaders = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` });

// scores is a JSON column, but some environments hand it back as a raw
// string rather than an already-parsed object — normalize defensively.
const parseScores = (scores) => {
    if (scores && typeof scores === "object") return scores;
    try {
        return JSON.parse(scores || "{}");
    } catch {
        return {};
    }
};

const ScoreBar = ({ label, value }) => (
    <div className="mb-2">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>{label}</span><span>{value}%</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2">
            <div className={`h-2 rounded-full ${value >= 70 ? "bg-green-500" : value >= 50 ? "bg-yellow-500" : "bg-red-500"}`} style={{ width: `${value}%` }} />
        </div>
    </div>
);

const EssayPractice = () => {
    const navigate = useNavigate();
    const [question, setQuestion] = useState("");
    const [response, setResponse] = useState("");
    const [grading, setGrading] = useState(false);
    const [result, setResult] = useState(null);
    const [activeHistoryId, setActiveHistoryId] = useState(null);
    const [history, setHistory] = useState([]);
    const resultRef = useRef(null);

    const fetchHistory = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/essay/history`, { headers: authHeaders() });
            const data = await res.json();
            setHistory((data.data || []).map((h) => ({ ...h, scores: parseScores(h.scores) })));
        } catch (err) {
            // silent
        }
    };

    useEffect(() => { fetchHistory(); }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!question.trim() || !response.trim()) {
            toast.error("Please enter both a question and your response");
            return;
        }
        setGrading(true);
        setResult(null);
        setActiveHistoryId(null);
        try {
            const res = await fetch(`${API_BASE_URL}/api/essay/submit`, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify({ question, response }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Failed to grade essay");
            setResult({ ...data.data, scores: parseScores(data.data.scores) });
            fetchHistory();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setGrading(false);
        }
    };

    const handleViewPast = (h) => {
        setResult(h);
        setActiveHistoryId(h.id);
        resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-blue-50/60 to-gray-50">
            <nav className="bg-[#2148C0] shadow-md px-6 py-4 flex items-center gap-4">
                <button onClick={() => navigate("/dashboard")} className="text-white"><FaArrowLeft size={18} /></button>
                <h1 className="text-xl font-bold text-white flex items-center gap-2"><FaPenFancy /> Essay Practice</h1>
            </nav>

            <div className="max-w-2xl mx-auto p-6 space-y-5">
                <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
                    <div>
                        <label className="text-xs font-medium text-gray-500 mb-1 block">Essay or theory question</label>
                        <textarea required placeholder="e.g. Discuss the causes of the Nigerian Civil War"
                            className="border border-gray-200 rounded-lg p-3 w-full text-sm focus:outline-none focus:ring-2 focus:ring-[#2148C0]/30" rows={2}
                            value={question} onChange={(e) => setQuestion(e.target.value)} />
                    </div>
                    <div>
                        <label className="text-xs font-medium text-gray-500 mb-1 block">Your response</label>
                        <textarea required placeholder="Write your response here..."
                            className="border border-gray-200 rounded-lg p-3 w-full text-sm focus:outline-none focus:ring-2 focus:ring-[#2148C0]/30" rows={8}
                            value={response} onChange={(e) => setResponse(e.target.value)} />
                    </div>
                    <button disabled={grading} className="w-full bg-blue600 text-white py-3 rounded-full font-semibold disabled:opacity-50 hover:opacity-90 transition">
                        {grading ? "Grading..." : "Submit for Grading"}
                    </button>
                </form>

                {result && (
                    <div ref={resultRef} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold text-gray-800">
                                {activeHistoryId ? "Past Submission" : "Score"}
                            </h3>
                            <span className="text-2xl font-bold text-blue600">{result.scores.overall}%</span>
                        </div>
                        {activeHistoryId && (
                            <p className="text-xs text-gray-400 mb-4 -mt-2 whitespace-pre-wrap">{result.question}</p>
                        )}
                        <ScoreBar label="Content" value={result.scores.content} />
                        <ScoreBar label="Grammar" value={result.scores.grammar} />
                        <ScoreBar label="Structure" value={result.scores.structure} />
                        <ScoreBar label="Relevance" value={result.scores.relevance} />
                        <ScoreBar label="Completeness" value={result.scores.completeness} />
                        <p className="text-sm text-gray-600 mt-4 border-t border-gray-100 pt-4 whitespace-pre-wrap">
                            {formatAiText(result.feedback)}
                        </p>
                    </div>
                )}

                {history.length > 0 && (
                    <div>
                        <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2 text-sm">
                            <FaHistory className="text-[#2148C0]" size={13} /> Past Submissions
                        </h3>
                        <div className="space-y-2">
                            {history.map((h) => (
                                <button
                                    key={h.id}
                                    onClick={() => handleViewPast(h)}
                                    className={`w-full text-left bg-white rounded-xl shadow-sm border p-3.5 flex justify-between items-center gap-3 transition ${
                                        activeHistoryId === h.id ? "border-[#2148C0]/40 bg-blue-50/40" : "border-gray-100 hover:border-gray-200"
                                    }`}
                                >
                                    <p className="text-sm text-gray-700 truncate flex-1">{h.question}</p>
                                    <span className="text-sm font-bold text-blue600 shrink-0">{h.scores?.overall ?? "-"}%</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EssayPractice;
